<script setup lang="ts">
import { getArticleList, getArticleDetail } from "@carnet/api/backoffice";
import type { ArticleResource, ArticleResourceDetailed } from "@carnet/api/backoffice";

const { t } = useI18n();

const [{ data: article }, { data: partnersData }] = await Promise.all([
  useAsyncData<ArticleResourceDetailed | null>(
    "article-partners",
    async () => {
      const response = await getArticleDetail({ composable: "$fetch", path: { locale: appLocale(), code: "partners" } });
      return response?.data ?? null;
    },
    { getCachedData: cachedDataUnlessRefresh }
  ),
  useAsyncData<ArticleResource[]>(
    "articles-partners-logos",
    async () => {
      const result = await getArticleList({ composable: "$fetch", path: { locale: appLocale() }, query: { context: "partnersLogos" } });
      return result ?? [];
    },
    { getCachedData: cachedDataUnlessRefresh }
  ),
]);

const partners = computed(() =>
  (partnersData.value ?? [])
    .filter((a): a is typeof a & { mainImage: { url: string } } => Boolean(a.mainImage?.url))
    .map((a) => ({ name: a.title, image: a.mainImage.url }))
);
</script>

<template>
  <AppSection v-if="article" :ui="{ base: 'pt-16 pb-20 sm:pt-10 sm:pb-25' }">
    <div class="not-last:mb-10">
      <h2 class="text-3xl sm:text-6xl font-semibold not-last:mb-2">
        {{ article.title }}
      </h2>
      <p v-if="article?.content" class="text-lg leading-relaxed">
        {{ article.content }}
      </p>
    </div>
    <ul v-if="partners.length" class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-px">
      <li
        v-for="partner in partners"
        :key="partner.image"
        class="max-[30em]:aspect-square relative flex items-center justify-center p-4 min-[30em]:p-6 before:absolute before:-left-px before:top-2 before:bottom-2 before:w-px before:bg-lagoon-300 after:absolute after:-top-px after:left-2 after:right-2 after:h-px after:bg-lagoon-300 max-sm:nth-[2n+1]:before:hidden sm:max-md:nth-[3n+1]:before:hidden md:nth-[4n+1]:before:hidden max-sm:nth-[-n+2]:after:hidden sm:max-md:nth-[-n+3]:after:hidden md:nth-[-n+4]:after:hidden"
      >
        <img :src="partner.image" :alt="partner.name || t('footer.partnerLogo')" class="max-h-28.5 max-w-46.5 h-auto w-full" />
      </li>
    </ul>
  </AppSection>
</template>
